import ExploreFilters from "components-for/filters/explore-filters";
import InteractionsTable from "components-for/interactions/interactions-table";
import InteractionsTableAgentColumn from "components-for/interactions/interactions-table-agent-column";
import InteractionsTableCurrentHandlingTimeColumn from "components-for/interactions/interactions-table-current-handling-time-column";
import InteractionsTableCurrentQueueTimeColumn from "components-for/interactions/interactions-table-current-queue-time-column";
import InteractionsTableMetricsColumn from "components-for/interactions/interactions-table-metrics-column";
import InteractionsTableStateColumn from "components-for/interactions/interactions-table-state-column";
import LiveAgentsTable from "./live-agents-table";
import LiveInteractionMetrics from "./live-interaction-metrics";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import useTags from "hooks-for/tags/use-tags";
import { FunctionComponent, memo } from "react";
import { InteractionMetric, InteractionState, ITag, ITagGroup } from "@edgetier/watchtower-types";
import { IProps } from "./live-interaction-data.types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { SpinnerUntil } from "@edgetier/components";
import { useLocation } from "react-router-dom";
import "./live-interaction-data.scss";

/**
 * Show live interaction data (queuing and interactions being handled).
 * @returns Live metrics, charts, and tables.
 */
const LiveInteractionData: FunctionComponent<IProps> = () => {
    const { data: tagGroups, isError: isTagGroupsError, isLoading: areTagGroupsLoading } = useTagGroups();
    const { data: tags, isError: isTagsError, isLoading: areTagsLoading } = useTags();

    // When the user changes filters, lots of things need to reload which is why this is passed as the "key" prop to one
    // of the components below. This was the simplest solution because it uses lots of queries with "keepPreviousData".
    const { search } = useLocation();

    return (
        <SpinnerUntil<{ tags: ITag[]; tagGroups: ITagGroup[] }>
            data={{ tagGroups, tags }}
            isReady={!areTagGroupsLoading && !areTagsLoading}
        >
            {(data) =>
                isTagsError || isTagGroupsError ? (
                    "Error"
                ) : (
                    <>
                        <ExploreFilters
                            hideFields={["agentId", "endDateTime", "startDateTime"]}
                            tags={data.tags || []}
                            tagGroups={(data.tagGroups || []).filter(({ isCommonFilter }) => isCommonFilter)}
                            submitOnChange={true}
                        />

                        <LiveInteractionMetrics key={search} />

                        <div className="live__columns">
                            <div>
                                <LiveAgentsTable />
                            </div>

                            <div>
                                <InteractionsTable
                                    defaultParameters={{
                                        interactionState: [
                                            InteractionState.Blocked,
                                            InteractionState.Handling,
                                            InteractionState.Queuing,
                                            InteractionState.WrapUp,
                                        ],
                                    }}
                                    queryOptions={{ refetchInterval: LIVE_POLL_INTERVAL_MILLISECONDS }}
                                    showDownloadButton={false}
                                    title="Live Interactions"
                                >
                                    <InteractionsTableAgentColumn />
                                    <InteractionsTableStateColumn />
                                    <InteractionsTableCurrentQueueTimeColumn />
                                    <InteractionsTableCurrentHandlingTimeColumn />
                                    <InteractionsTableMetricsColumn
                                        interactionMetric={InteractionMetric.FirstResponseTime}
                                    />
                                </InteractionsTable>
                            </div>
                        </div>
                    </>
                )
            }
        </SpinnerUntil>
    );
};

export default memo(LiveInteractionData);
